import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Tooltip,
  Chip,
  Switch,
  FormControlLabel,
  TextField,
  MenuItem,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import SendIcon from '@mui/icons-material/Send';
import EditIcon from '@mui/icons-material/Edit';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import CloseIcon from '@mui/icons-material/Close';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import ForumIcon from '@mui/icons-material/Forum';
import PaymentsIcon from '@mui/icons-material/Payments';
import BlockIcon from '@mui/icons-material/Block';
import GavelIcon from '@mui/icons-material/Gavel';
import FolderZipIcon from '@mui/icons-material/FolderZip';
import dayjs from 'dayjs';

import EditableList from '../EditableList/EditableList';
import InvoiceAddressDialog from '../Invoices/InvoiceAddressDialog';
import InvoiceCancelDialog from '../Invoices/InvoiceCancelDialog';
import InvoiceMailHistoryModal from '../Invoices/InvoiceMailHistoryModal';
import useInvoice from '../../hooks/useInvoice';
import useBranch from '../../hooks/useBranch';
import AuthContext from '../../context/authContext';
import { toast } from 'react-toastify';
import ToolbarToggleChip from '../components/ToolbarToggleChip';
import { viewRootTextPrimarySx } from '../../util/chartTheme';

const hasInvoiceDeliveryIssue = invoice => {
  const md = invoice?.mailDelivery;
  const ebs = invoice?.emailBounceState;
  const isMailFailed = md && (md.status === 'failed' || (md.failedCount || 0) > 0);
  const isAddressDead = Boolean(ebs?.isSuppressed);
  return Boolean(isMailFailed || isAddressDead);
};

// No-show dunning level ("Mahnstufe") options + chip colors.
const DUNNING_STATUS_OPTIONS = [
  { value: 'none', label: 'Offen' },
  { value: 'reminded', label: 'Zahlungserinnerung' },
  { value: 'overdue', label: 'Frist überschritten' },
  { value: 'waived', label: 'Erlassen' },
];
const DUNNING_STATUS_COLOR = {
  none: 'default',
  reminded: 'warning',
  overdue: 'error',
  waived: 'default',
};

// Creditreform ("krefo") handover status options + chip colors.
const KREFO_STATUS_OPTIONS = [
  { value: 'notHandedOver', label: 'Nicht übergeben' },
  { value: 'handedOver', label: 'An Crefo übergeben' },
  { value: 'paidAfterHandover', label: 'Übergeben – trotzdem bezahlt' },
];
const KREFO_STATUS_COLOR = {
  notHandedOver: 'default',
  handedOver: 'info',
  paidAfterHandover: 'error',
};

// Invoice origins whose collection cycle runs automatically (reminder mail, payment
// deadline, overdue cron) — mirrors the backend's collectable origins.
const isCollectableOrigin = origin => origin === 'noShow' || origin === 'unpaidBooking';

// Whether an invoice takes part in the dunning / Creditreform collection workflow:
// collectable origins always do; any other original invoice does once it is unpaid or
// already carries a non-default collection status (manual Creditreform handover).
const showsCollectionStatus = inv => {
  if (!inv || inv.type === 'cancellation') return false;
  if (isCollectableOrigin(inv.origin)) return true;
  return (
    inv.paymentStatus === 'unpaid' ||
    (inv.dunningStatus || 'none') !== 'none' ||
    (inv.krefoStatus || 'notHandedOver') !== 'notHandedOver'
  );
};

const labelFor = (options, value) => options.find(o => o.value === value)?.label || value || '—';

function InvoiceManagement() {
  const authCtx = useContext(AuthContext);
  const { getBranches } = useBranch();
  const {
    markInvoicePaid,
    confirmNoShowPayment,
    cancelNoShowProcess,
    updateCollectionStatus,
    downloadKrefoZip,
    getInvoicePDFById,
    updateInvoiceCustomer,
    sendInvoiceMail,
    createCancellation,
    reissueWithCancellation,
    isLoading,
  } = useInvoice();

  const [group, setGroup] = useState(true);
  const [branchOptions, setBranchOptions] = useState([]);

  const [addressDialog, setAddressDialog] = useState({ open: false, invoice: null });
  const [cancelDialog, setCancelDialog] = useState({ open: false, invoice: null });
  const [markPaidDialog, setMarkPaidDialog] = useState({ open: false, invoice: null });
  const [mailHistoryDialog, setMailHistoryDialog] = useState({ open: false, invoice: null });
  const [isConfirmingMarkPaid, setIsConfirmingMarkPaid] = useState(false);

  const [sendMailDialog, setSendMailDialog] = useState({ open: false, invoice: null });
  const [isSendingMail, setIsSendingMail] = useState(false);

  const [noShowPaymentDialog, setNoShowPaymentDialog] = useState({ open: false, invoice: null });
  const [grantKulanzVoucher, setGrantKulanzVoucher] = useState(false);
  const [isConfirmingNoShowPayment, setIsConfirmingNoShowPayment] = useState(false);

  const [noShowCancelDialog, setNoShowCancelDialog] = useState({ open: false, invoice: null });
  const [noShowCancelReason, setNoShowCancelReason] = useState('');
  const [isCancellingNoShow, setIsCancellingNoShow] = useState(false);

  const [collectionStatusDialog, setCollectionStatusDialog] = useState({ open: false, invoice: null });
  const [dunningStatusValue, setDunningStatusValue] = useState('none');
  const [krefoStatusValue, setKrefoStatusValue] = useState('notHandedOver');
  const [isSavingCollectionStatus, setIsSavingCollectionStatus] = useState(false);

  const [krefoExportDialogOpen, setKrefoExportDialogOpen] = useState(false);
  const [isExportingKrefo, setIsExportingKrefo] = useState(false);

  const [pdfViewerOpen, setPdfViewerOpen] = useState(false);
  const [currentInvoice, setCurrentInvoice] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);

  const getBranchesRef = useRef(getBranches);
  useEffect(() => {
    getBranchesRef.current = getBranches;
  }, [getBranches]);

  useEffect(() => {
    let isMounted = true;
    const init = async () => {
      try {
        const branches = await getBranchesRef.current();
        if (!isMounted) return;
        setBranchOptions((branches || []).map(b => ({ value: b._id, label: b.locationName })));
      } catch (_) {
        if (!isMounted) return;
        setBranchOptions([]);
      }
    };
    init();
    return () => {
      isMounted = false;
    };
  }, []);

  const getBranchValue = useCallback(inv => {
    const b = inv?.branchId;
    if (!b) return '';
    if (typeof b === 'string') return b;
    return b._id || b.id || '';
  }, []);

  const handleDownload = useCallback(
    async invoice => {
      try {
        const blob = await getInvoicePDFById(invoice._id);
        if (blob) {
          const url = window.URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }));
          const a = document.createElement('a');
          a.href = url;
          a.download = `invoice-${invoice.invoiceNumber}.pdf`;
          document.body.appendChild(a);
          a.click();
          window.URL.revokeObjectURL(url);
          document.body.removeChild(a);
        }
      } catch (e) {
        toast.error('Fehler beim Herunterladen der Rechnung.');
      }
    },
    [getInvoicePDFById]
  );

  const handleView = useCallback(
    async invoice => {
      try {
        setCurrentInvoice(invoice);
        const blob = await getInvoicePDFById(invoice._id);
        if (blob) {
          if (pdfUrl) window.URL.revokeObjectURL(pdfUrl);
          const url = window.URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }));
          setPdfUrl(url);
          setPdfViewerOpen(true);
        }
      } catch (e) {
        setCurrentInvoice(null);
        toast.error('Fehler beim Anzeigen der Rechnung.');
      }
    },
    [getInvoicePDFById, pdfUrl]
  );

  const handleClosePdfViewer = useCallback(() => {
    setPdfViewerOpen(false);
    setCurrentInvoice(null);
    if (pdfUrl) window.URL.revokeObjectURL(pdfUrl);
    setPdfUrl(null);
  }, [pdfUrl]);

  const refreshInvoiceList = useCallback(() => {
    window.dispatchEvent(
      new CustomEvent('editableListRefresh', { detail: { resource: 'invoice' } })
    );
  }, []);

  const handleMarkPaid = useCallback(
    async invoice => {
      try {
        await markInvoicePaid(invoice._id);
        toast.success('Rechnung als bezahlt markiert.');
        refreshInvoiceList();
      } catch (e) {
        toast.error('Fehler beim Markieren als bezahlt.');
      }
    },
    [markInvoicePaid, refreshInvoiceList]
  );

  const handleRequestMarkPaid = useCallback(invoice => {
    if (!invoice?._id) return;
    if (invoice.paymentStatus === 'paid') return;
    setMarkPaidDialog({ open: true, invoice });
  }, []);

  const handleCloseMarkPaidDialog = useCallback(() => {
    if (isConfirmingMarkPaid) return;
    setMarkPaidDialog({ open: false, invoice: null });
  }, [isConfirmingMarkPaid]);

  const handleConfirmMarkPaid = useCallback(async () => {
    const invoice = markPaidDialog.invoice;
    if (!invoice?._id) {
      setMarkPaidDialog({ open: false, invoice: null });
      return;
    }
    if (invoice.paymentStatus === 'paid') {
      setMarkPaidDialog({ open: false, invoice: null });
      return;
    }

    setIsConfirmingMarkPaid(true);
    try {
      await handleMarkPaid(invoice);
      setMarkPaidDialog({ open: false, invoice: null });
    } finally {
      setIsConfirmingMarkPaid(false);
    }
  }, [handleMarkPaid, markPaidDialog.invoice]);

  const handleRequestNoShowPayment = useCallback(invoice => {
    if (!invoice?._id) return;
    setGrantKulanzVoucher(false);
    setNoShowPaymentDialog({ open: true, invoice });
  }, []);

  const handleCloseNoShowPaymentDialog = useCallback(() => {
    if (isConfirmingNoShowPayment) return;
    setNoShowPaymentDialog({ open: false, invoice: null });
  }, [isConfirmingNoShowPayment]);

  const handleConfirmNoShowPayment = useCallback(async () => {
    const invoice = noShowPaymentDialog.invoice;
    if (!invoice?._id) {
      setNoShowPaymentDialog({ open: false, invoice: null });
      return;
    }
    setIsConfirmingNoShowPayment(true);
    try {
      await confirmNoShowPayment(invoice._id, grantKulanzVoucher);
      toast.success(
        grantKulanzVoucher
          ? 'Zahlungseingang bestätigt und Kulanz-Gutschein erstellt.'
          : 'Zahlungseingang bestätigt.'
      );
      setNoShowPaymentDialog({ open: false, invoice: null });
      refreshInvoiceList();
    } catch (e) {
      toast.error('Fehler beim Bestätigen des Zahlungseingangs.');
    } finally {
      setIsConfirmingNoShowPayment(false);
    }
  }, [confirmNoShowPayment, grantKulanzVoucher, noShowPaymentDialog.invoice, refreshInvoiceList]);

  const handleRequestNoShowCancel = useCallback(invoice => {
    if (!invoice?._id) return;
    setNoShowCancelReason('');
    setNoShowCancelDialog({ open: true, invoice });
  }, []);

  const handleCloseNoShowCancelDialog = useCallback(() => {
    if (isCancellingNoShow) return;
    setNoShowCancelDialog({ open: false, invoice: null });
  }, [isCancellingNoShow]);

  const handleConfirmNoShowCancel = useCallback(async () => {
    const invoice = noShowCancelDialog.invoice;
    if (!invoice?._id) {
      setNoShowCancelDialog({ open: false, invoice: null });
      return;
    }
    setIsCancellingNoShow(true);
    try {
      const result = await cancelNoShowProcess(invoice._id, noShowCancelReason.trim());
      toast.success(
        result?.alreadyCancelled
          ? 'Vorgang war bereits abgebrochen.'
          : 'Vorgang abgebrochen und Stornorechnung erstellt.'
      );
      setNoShowCancelDialog({ open: false, invoice: null });
      refreshInvoiceList();
    } catch (e) {
      toast.error('Fehler beim Abbrechen des Vorgangs.');
      // The cancellation may have completed even though the response was lost (e.g.
      // a flaky POS connection). Refresh so the list reflects the real state instead
      // of leaving a stale "still open" view that hides the already-done cancellation.
      refreshInvoiceList();
    } finally {
      setIsCancellingNoShow(false);
    }
  }, [cancelNoShowProcess, noShowCancelDialog.invoice, noShowCancelReason, refreshInvoiceList]);

  const handleRequestEditCollectionStatus = useCallback(invoice => {
    if (!invoice?._id) return;
    setDunningStatusValue(invoice.dunningStatus || 'none');
    setKrefoStatusValue(invoice.krefoStatus || 'notHandedOver');
    setCollectionStatusDialog({ open: true, invoice });
  }, []);

  const handleCloseCollectionStatusDialog = useCallback(() => {
    if (isSavingCollectionStatus) return;
    setCollectionStatusDialog({ open: false, invoice: null });
  }, [isSavingCollectionStatus]);

  const handleConfirmEditCollectionStatus = useCallback(async () => {
    const invoice = collectionStatusDialog.invoice;
    if (!invoice?._id) {
      setCollectionStatusDialog({ open: false, invoice: null });
      return;
    }
    setIsSavingCollectionStatus(true);
    try {
      await updateCollectionStatus(invoice._id, {
        dunningStatus: dunningStatusValue,
        krefoStatus: krefoStatusValue,
      });
      toast.success('Mahnstufe und Crefo-Status aktualisiert.');
      setCollectionStatusDialog({ open: false, invoice: null });
      refreshInvoiceList();
    } catch (e) {
      toast.error('Fehler beim Aktualisieren des Status.');
    } finally {
      setIsSavingCollectionStatus(false);
    }
  }, [collectionStatusDialog.invoice, dunningStatusValue, krefoStatusValue, updateCollectionStatus, refreshInvoiceList]);

  const handleConfirmKrefoExport = useCallback(async () => {
    setIsExportingKrefo(true);
    try {
      const blob = await downloadKrefoZip();
      // null = request was stored for rights escalation; nothing to download yet.
      if (blob) {
        const url = window.URL.createObjectURL(new Blob([blob], { type: 'application/zip' }));
        const a = document.createElement('a');
        a.href = url;
        a.download = `krefo-export-${dayjs().format('YYYY-MM-DD')}.zip`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        toast.success(
          'Crefo-Export erstellt. Enthaltene Rechnungen wurden als „an Crefo übergeben" markiert.'
        );
        setKrefoExportDialogOpen(false);
        refreshInvoiceList();
      }
    } catch (e) {
      const msg =
        e?.code === 'KREFO_EXPORT_EMPTY' || e?.code === 'KREFO_EXPORT_NO_PDFS'
          ? 'Keine überfälligen, noch nicht übergebenen Rechnungen zum Export.'
          : 'Fehler beim Erstellen des Crefo-Exports.';
      toast.error(msg);
    } finally {
      setIsExportingKrefo(false);
    }
  }, [downloadKrefoZip, refreshInvoiceList]);

  const handleSendMail = useCallback(
    async invoice => {
      try {
        await sendInvoiceMail(invoice._id);
        toast.success('Rechnung per E-Mail versendet.');
      } catch (e) {
        toast.error('Fehler beim E-Mail-Versand der Rechnung.');
      }
    },
    [sendInvoiceMail]
  );

  const handleRequestSendMail = useCallback(invoice => {
    if (!invoice?._id) return;
    setSendMailDialog({ open: true, invoice });
  }, []);

  const handleCloseSendMailDialog = useCallback(() => {
    if (isSendingMail) return;
    setSendMailDialog({ open: false, invoice: null });
  }, [isSendingMail]);

  const handleConfirmSendMail = useCallback(async () => {
    const invoice = sendMailDialog.invoice;
    if (!invoice?._id || !invoice.customerData?.mail) {
      setSendMailDialog({ open: false, invoice: null });
      return;
    }
    setIsSendingMail(true);
    try {
      await handleSendMail(invoice);
      setSendMailDialog({ open: false, invoice: null });
    } finally {
      setIsSendingMail(false);
    }
  }, [handleSendMail, sendMailDialog.invoice]);

  const invoiceSchema = useMemo(
    () => ({
      invoiceNumber: {
        label: 'Rechnungsnr.',
        type: 'text',
        required: false,
        minWidth: '180px',
        readOnly: true,
        headerExtra: () => (
          <ToolbarToggleChip
            checked={group}
            onChange={next => setGroup(next)}
            label='Grp.'
            tooltip={
              group
                ? 'Gruppieren ist aktiv (Korrekturen unter Original)'
                : 'Gruppieren ist aus'
            }
          />
        ),
        listDisplayValue: (value, item) => {
          const md = item?.mailDelivery;
          const ebs = item?.emailBounceState;
          const isMailFailed = md && (md.status === 'failed' || (md.failedCount || 0) > 0);
          const isAddressDead = Boolean(ebs?.isSuppressed);
          if (!isMailFailed && !isAddressDead) return value || '—';

          let title;
          if (isAddressDead) {
            const since = ebs?.lastHardBounceAt
              ? dayjs(ebs.lastHardBounceAt).format('DD.MM.YYYY HH:mm')
              : null;
            const errPart = ebs?.lastErrorMessage ? ` – ${ebs.lastErrorMessage}` : '';
            title = `Empfängeradresse gesperrt${since ? ` seit ${since}` : ''}${errPart}`;
          } else {
            const lastFailedAt = md?.lastFailedAt
              ? dayjs(md.lastFailedAt).format('DD.MM.YYYY HH:mm')
              : null;
            const errPart = md?.lastErrorMessage ? `: ${md.lastErrorMessage}` : '';
            const datePart = lastFailedAt ? ` (${lastFailedAt})` : '';
            title = `Letzte Mail an Kunde nicht zustellbar${datePart}${errPart}`;
          }
          return (
            <Box component='span' sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
              <Tooltip title={title}>
                <ReportProblemIcon fontSize='small' sx={{ color: 'error.main' }} />
              </Tooltip>
              <Typography component='span' variant='body2'>
                {value || '—'}
              </Typography>
            </Box>
          );
        },
      },
      // Booking of this invoice. The value comes from the backend (resolved from
      // relatedBookingId); filtering by it is resolved back to bookings server-side.
      bookingNumber: {
        label: 'Buchungsnr.',
        type: 'text',
        required: false,
        minWidth: '140px',
        readOnly: true,
        listDisplayValue: value => value || '—',
      },
      branchId: {
        label: 'Filiale',
        type: 'select',
        required: false,
        minWidth: '180px',
        readOnly: true,
        options: branchOptions,
      },
      createdAt: {
        label: 'Datum',
        type: 'text',
        readOnly: true,
        minWidth: '120px',
        listDisplayValue: v => (v ? dayjs(v).format('DD.MM.YYYY') : '—'),
        sortType: 'date',
        sortValue: v => (v ? Date.parse(String(v)) : null),
        filterType: 'date',
        filterValue: v => (v ? dayjs(v).format('YYYY-MM-DD') : ''),
      },
      customer: { label: 'Kunde', type: 'text', required: false, minWidth: '220px', readOnly: true },
      customerMail: {
        label: 'E-Mail',
        type: 'text',
        required: false,
        minWidth: '220px',
        readOnly: true,
        copyable: true,
        copyTooltip: 'E-Mail-Adresse kopieren',
        copyToastMessage: 'E-Mail-Adresse kopiert.',
        truncate: 32,
        truncateMiddle: true,
      },
      deliveryStatus: {
        label: 'Zustellung',
        type: 'select',
        required: false,
        minWidth: '160px',
        readOnly: true,
        options: [{ value: 'failed', label: 'Nicht zustellbar' }],
        listDisplayValue: value =>
          value === 'failed' ? (
            <Chip label='Nicht zustellbar' color='error' size='small' variant='outlined' />
          ) : (
            '—'
          ),
      },
      origin: {
        label: 'Herkunft',
        type: 'select',
        required: false,
        minWidth: '160px',
        readOnly: true,
        options: [
          { value: 'pos', label: 'Kasse' },
          { value: 'externalPos', label: 'Externe Kasse' },
          { value: 'online', label: 'Buchungssystem' },
          { value: 'voucher', label: 'Gutschein' },
          { value: 'noShow', label: 'No-Show' },
          { value: 'forgottenPayment', label: 'Vergessene Zahlung' },
          { value: 'unpaidBooking', label: 'Unbezahlte Buchung' },
        ],
      },
      status: {
        label: 'Status',
        type: 'select',
        required: false,
        minWidth: '160px',
        readOnly: true,
        options: [
          { value: 'draft', label: 'Entwurf' },
          { value: 'sent', label: 'Gesendet' },
          { value: 'fully_refunded', label: 'Vollständig erstattet' },
          { value: 'partially_refunded', label: 'Teilweise erstattet' },
          { value: 'is_refund', label: 'Erstattung' },
        ],
      },
      paymentStatus: {
        label: 'Zahlung',
        type: 'select',
        required: false,
        minWidth: '120px',
        readOnly: true,
        options: [
          { value: 'paid', label: 'Bezahlt' },
          { value: 'unpaid', label: 'Offen' },
        ],
      },
      hasKulanzVoucher: {
        label: 'Gutschein',
        type: 'boolean',
        required: false,
        minWidth: '110px',
        readOnly: true,
        listDisplayValue: (value, item) => {
          if (item?.origin !== 'noShow') return '—';
          return value ? (
            <Chip label='Erhalten' color='success' size='small' variant='outlined' />
          ) : (
            <Chip label='Kein' size='small' variant='outlined' />
          );
        },
      },
      dunningStatus: {
        label: 'Mahnstufe',
        type: 'select',
        required: false,
        minWidth: '180px',
        readOnly: true,
        options: DUNNING_STATUS_OPTIONS,
        listDisplayValue: (value, item) => {
          if (!showsCollectionStatus(item?.__raw)) return '—';
          return (
            <Chip
              label={labelFor(DUNNING_STATUS_OPTIONS, value || 'none')}
              color={DUNNING_STATUS_COLOR[value] || 'default'}
              size='small'
              variant='outlined'
            />
          );
        },
      },
      krefoStatus: {
        label: 'Crefo',
        type: 'select',
        required: false,
        minWidth: '220px',
        readOnly: true,
        options: KREFO_STATUS_OPTIONS,
        listDisplayValue: (value, item) => {
          if (!showsCollectionStatus(item?.__raw)) return '—';
          return (
            <Chip
              label={labelFor(KREFO_STATUS_OPTIONS, value || 'notHandedOver')}
              color={KREFO_STATUS_COLOR[value] || 'default'}
              size='small'
              variant='outlined'
            />
          );
        },
      },
      totalGross: {
        label: 'Betrag',
        type: 'price',
        required: false,
        minWidth: '140px',
        readOnly: true,
      },
      mailDelivery: { label: 'mailDelivery', type: 'object', excludeFromList: true },
      emailBounceState: { label: 'emailBounceState', type: 'object', excludeFromList: true },
      __isChild: { label: '__isChild', type: 'boolean', excludeFromList: true },
      __raw: { label: '__raw', type: 'object', excludeFromList: true },
    }),
    [branchOptions, group]
  );

  const transformItems = useCallback(item => {
    const mkRow = (inv, isChild) => ({
      _id: inv._id,
      invoiceNumber: isChild ? `↳ ${inv.invoiceNumber}` : inv.invoiceNumber,
      bookingNumber: inv.bookingNumber || '',
      branchId: getBranchValue(inv),
      createdAt: inv.meta?.createdAt || null,
      customer: `${inv.customerData?.company ? `${inv.customerData.company} · ` : ''}${inv.customerData?.firstName || ''} ${inv.customerData?.lastName || ''}`.trim(),
      customerMail: inv.customerData?.mail || '',
      deliveryStatus: hasInvoiceDeliveryIssue(inv) ? 'failed' : '',
      origin: inv.origin || '',
      status: inv.status || '',
      paymentStatus: inv.paymentStatus || '',
      hasKulanzVoucher: Boolean(inv.hasKulanzVoucher),
      dunningStatus: inv.dunningStatus || 'none',
      krefoStatus: inv.krefoStatus || 'notHandedOver',
      totalGross: inv.totals?.totalGross,
      mailDelivery: inv.mailDelivery || null,
      emailBounceState: inv.emailBounceState || null,
      __isChild: Boolean(isChild),
      __raw: inv,
    });

    const rows = [mkRow(item, false)];
    if (Array.isArray(item.children) && item.children.length > 0) {
      item.children.forEach(child => rows.push(mkRow(child, true)));
    }
    return rows;
  }, [getBranchValue]);

  const getRowSx = useCallback(item => {
    if (!hasInvoiceDeliveryIssue(item)) return null;
    return {
      backgroundColor: 'rgba(244, 67, 54, 0.12) !important',
      '&:hover': { backgroundColor: 'rgba(244, 67, 54, 0.20) !important' },
      '& td': { color: 'error.light' },
    };
  }, []);

  const renderRowActions = useCallback(
    row => {
      const inv = row?.__raw;
      if (!inv) return null;

      const isChild = Boolean(row.__isChild);
      const isOpenNoShow =
        !isChild &&
        inv.origin === 'noShow' &&
        inv.paymentStatus !== 'paid' &&
        inv.status !== 'fully_refunded';
      // The cancel-process action covers every collectable origin (no-show AND
      // unpaid-booking, e.g. a retained ticket insurance); confirming a manual
      // payment stays no-show-only (unpaid-booking uses "Als bezahlt markieren").
      const isOpenCollectable =
        !isChild &&
        isCollectableOrigin(inv.origin) &&
        inv.paymentStatus !== 'paid' &&
        inv.status !== 'fully_refunded';
      // Cancelling positions is offered for every original invoice, not only for the ones
      // flagged isEditable (POS delivery notes, unpaid external and staff-issued
      // receivables): an online booking, a POS receipt or a voucher invoice can need a
      // cancellation document just as well, and the endpoint behind it builds the storno
      // from any invoice. Cancellation documents themselves and fully refunded invoices
      // stay out — a storno carries only negative positions, so there is nothing left to
      // cancel on it.
      const canCancelPositions =
        !isChild &&
        inv.type !== 'cancellation' &&
        authCtx.checkRightAny('invoice:update') &&
        inv.status !== 'fully_refunded';

      return (
        <>
          <Tooltip title='Anzeigen'>
            <IconButton size='small' onClick={() => handleView(inv)}>
              <VisibilityIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title='Herunterladen'>
            <IconButton size='small' onClick={() => handleDownload(inv)}>
              <DownloadIcon />
            </IconButton>
          </Tooltip>
          {inv.origin !== 'noShow' && authCtx.checkRightAny('invoice:special:markPaid') && (
            <Tooltip title='Als bezahlt markieren'>
              <span>
                <IconButton
                  size='small'
                  onClick={() => handleRequestMarkPaid(inv)}
                  disabled={inv.paymentStatus === 'paid'}
                >
                  <CheckCircleOutlineIcon />
                </IconButton>
              </span>
            </Tooltip>
          )}
          {authCtx.checkRight('invoice:read:own') && (
            <Tooltip title='Rechnung per E-Mail senden'>
              <IconButton size='small' onClick={() => handleRequestSendMail(inv)}>
                <SendIcon />
              </IconButton>
            </Tooltip>
          )}
          {authCtx.checkRight('invoice:read:own') && (
            <Tooltip title='Mailverlauf anzeigen'>
              <IconButton
                size='small'
                onClick={() => setMailHistoryDialog({ open: true, invoice: inv })}
              >
                <ForumIcon />
              </IconButton>
            </Tooltip>
          )}
          {!isChild &&
            authCtx.checkRightAny('invoice:update:customerData') &&
            inv.status !== 'fully_refunded' && (
              <Tooltip title='Adresse bearbeiten'>
                <IconButton size='small' onClick={() => setAddressDialog({ open: true, invoice: inv })}>
                  <EditIcon />
                </IconButton>
              </Tooltip>
            )}
          {canCancelPositions && (
            <Tooltip title='Positionen stornieren'>
              <IconButton size='small' onClick={() => setCancelDialog({ open: true, invoice: inv })}>
                <ReceiptLongIcon />
              </IconButton>
            </Tooltip>
          )}
          {isOpenNoShow && authCtx.checkRightAny('invoice:special:confirmNoShowPayment') && (
            <Tooltip title='Zahlungseingang bestätigen (No-Show)'>
              <IconButton size='small' color='success' onClick={() => handleRequestNoShowPayment(inv)}>
                <PaymentsIcon />
              </IconButton>
            </Tooltip>
          )}
          {isOpenCollectable && authCtx.checkRightAny('invoice:special:cancelNoShowProcess') && (
            <Tooltip
              title={
                inv.origin === 'noShow'
                  ? 'No-Show-Vorgang abbrechen'
                  : 'Forderung erlassen & stornieren'
              }
            >
              <IconButton size='small' color='error' onClick={() => handleRequestNoShowCancel(inv)}>
                <BlockIcon />
              </IconButton>
            </Tooltip>
          )}
          {!isChild &&
            showsCollectionStatus(inv) &&
            authCtx.checkRightAny('invoice:special:editCollectionStatus') && (
              <Tooltip title='Mahnstufe / Crefo-Status bearbeiten'>
                <IconButton size='small' onClick={() => handleRequestEditCollectionStatus(inv)}>
                  <GavelIcon />
                </IconButton>
              </Tooltip>
            )}
        </>
      );
    },
    [
      authCtx,
      handleDownload,
      handleRequestEditCollectionStatus,
      handleRequestMarkPaid,
      handleRequestNoShowCancel,
      handleRequestNoShowPayment,
      handleRequestSendMail,
      handleView,
    ]
  );

  return (
    <Box sx={{ ...viewRootTextPrimarySx }}>
      {authCtx.checkRightAny('invoice:special:exportKrefo') && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
          <Button
            variant='outlined'
            startIcon={<FolderZipIcon />}
            onClick={() => setKrefoExportDialogOpen(true)}
          >
            Crefo-Export (ZIP)
          </Button>
        </Box>
      )}
      <EditableList
        title='Rechnungen'
        resourceType='invoice'
        schema={invoiceSchema}
        searchPlaceholder='Rechnungen durchsuchen'
        options={{
          disableCreate: true,
          disableDelete: true,
          disableDetailView: true,
          listConfig: {
            endpoint: '/api/v1/invoice/list/all',
            searchParam: 'q',
            resultsKey: 'items',
            pageParam: 'page',
            limitParam: 'limit',
          },
          transformItems,
          serverSideFiltering: true,
          serverSideSorting: true,
        }}
        listParams={{ group: group ? 'true' : 'false' }}
        renderRowActions={renderRowActions}
        getRowSx={getRowSx}
        hideSearchField={true}
      />

      <InvoiceAddressDialog
        open={addressDialog.open}
        invoice={addressDialog.invoice}
        onClose={() => setAddressDialog({ open: false, invoice: null })}
        onSave={() => window.dispatchEvent(new CustomEvent('editableListRefresh', { detail: { resource: 'invoice' } }))}
        updateInvoiceCustomer={updateInvoiceCustomer}
        reissueWithCancellation={reissueWithCancellation}
      />
      <InvoiceCancelDialog
        open={cancelDialog.open}
        invoice={cancelDialog.invoice}
        onClose={() => setCancelDialog({ open: false, invoice: null })}
        onSaved={() => window.dispatchEvent(new CustomEvent('editableListRefresh', { detail: { resource: 'invoice' } }))}
        createCancellation={createCancellation}
      />

      <InvoiceMailHistoryModal
        open={mailHistoryDialog.open}
        invoice={mailHistoryDialog.invoice}
        onClose={() => setMailHistoryDialog({ open: false, invoice: null })}
      />

      <Dialog
        open={markPaidDialog.open}
        onClose={handleCloseMarkPaidDialog}
        maxWidth='xs'
        fullWidth
      >
        <DialogTitle>Rechnung als bezahlt markieren?</DialogTitle>
        <DialogContent>
          <Typography variant='body2' color='text.secondary' sx={{ mb: 1.5 }}>
            Dadurch wird der Zahlungsstatus dauerhaft auf „Bezahlt“ gesetzt.
          </Typography>
          {markPaidDialog.invoice && (
            <Box sx={{ display: 'grid', gap: 0.5 }}>
              <Typography variant='body2'>
                <strong>Rechnung:</strong> {markPaidDialog.invoice.invoiceNumber}
              </Typography>
              <Typography variant='body2'>
                <strong>Datum:</strong>{' '}
                {markPaidDialog.invoice?.meta?.createdAt
                  ? dayjs(markPaidDialog.invoice.meta.createdAt).format('DD.MM.YYYY')
                  : '-'}
              </Typography>
              <Typography variant='body2'>
                <strong>Betrag:</strong>{' '}
                {typeof markPaidDialog.invoice?.totals?.totalGross === 'number'
                  ? markPaidDialog.invoice.totals.totalGross.toFixed(2) + ' €'
                  : '-'}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseMarkPaidDialog} disabled={isConfirmingMarkPaid || isLoading}>
            Abbrechen
          </Button>
          <Button
            variant='contained'
            onClick={handleConfirmMarkPaid}
            disabled={
              isConfirmingMarkPaid ||
              isLoading ||
              markPaidDialog.invoice?.paymentStatus === 'paid'
            }
          >
            Als bezahlt markieren
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={sendMailDialog.open}
        onClose={handleCloseSendMailDialog}
        maxWidth='xs'
        fullWidth
      >
        <DialogTitle>Rechnung per E-Mail senden?</DialogTitle>
        <DialogContent>
          <Typography variant='body2' color='text.secondary' sx={{ mb: 1.5 }}>
            Die Rechnung wird als PDF-Anhang an die hinterlegte Kunden-E-Mail-Adresse gesendet.
          </Typography>
          {sendMailDialog.invoice && (
            <Box sx={{ display: 'grid', gap: 0.5 }}>
              <Typography variant='body2'>
                <strong>Rechnung:</strong> {sendMailDialog.invoice.invoiceNumber}
              </Typography>
              <Typography variant='body2'>
                <strong>Empfänger:</strong>{' '}
                {sendMailDialog.invoice.customerData?.mail || '— keine E-Mail hinterlegt —'}
              </Typography>
              <Typography variant='body2'>
                <strong>Betrag:</strong>{' '}
                {typeof sendMailDialog.invoice?.totals?.totalGross === 'number'
                  ? sendMailDialog.invoice.totals.totalGross.toFixed(2) + ' €'
                  : '-'}
              </Typography>
            </Box>
          )}
          {sendMailDialog.invoice && !sendMailDialog.invoice.customerData?.mail && (
            <Typography variant='body2' sx={{ mt: 1.5, fontWeight: 600, color: 'error.main' }}>
              Für diese Rechnung ist keine E-Mail-Adresse hinterlegt. Bitte zuerst die Adresse
              bearbeiten.
            </Typography>
          )}
          {sendMailDialog.invoice &&
            sendMailDialog.invoice.customerData?.mail &&
            hasInvoiceDeliveryIssue(sendMailDialog.invoice) && (
              <Typography variant='body2' sx={{ mt: 1.5, fontWeight: 600, color: 'warning.main' }}>
                Hinweis: Für diese Rechnung gab es zuletzt ein Zustellproblem (Bounce bzw. gesperrte
                Adresse). Bitte die E-Mail-Adresse vor dem Versand prüfen.
              </Typography>
            )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseSendMailDialog} disabled={isSendingMail || isLoading}>
            Abbrechen
          </Button>
          <Button
            variant='contained'
            startIcon={<SendIcon />}
            onClick={handleConfirmSendMail}
            disabled={isSendingMail || isLoading || !sendMailDialog.invoice?.customerData?.mail}
          >
            Senden
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={noShowPaymentDialog.open}
        onClose={handleCloseNoShowPaymentDialog}
        maxWidth='xs'
        fullWidth
      >
        <DialogTitle>Zahlungseingang bestätigen?</DialogTitle>
        <DialogContent>
          <Typography variant='body2' color='text.secondary' sx={{ mb: 1.5 }}>
            Bestätigt einen außerhalb von Adyen erhaltenen Zahlungseingang (z.&nbsp;B. bar oder
            per Überweisung). Die Rechnung wird auf „Bezahlt“ gesetzt, der No-Show-Vorgang
            abgeschlossen und die Erinnerungs-E-Mails gestoppt.
          </Typography>
          {noShowPaymentDialog.invoice && (
            <Box sx={{ display: 'grid', gap: 0.5, mb: 1 }}>
              <Typography variant='body2'>
                <strong>Rechnung:</strong> {noShowPaymentDialog.invoice.invoiceNumber}
              </Typography>
              <Typography variant='body2'>
                <strong>Betrag:</strong>{' '}
                {typeof noShowPaymentDialog.invoice?.totals?.totalGross === 'number'
                  ? noShowPaymentDialog.invoice.totals.totalGross.toFixed(2) + ' €'
                  : '-'}
              </Typography>
            </Box>
          )}
          <FormControlLabel
            control={
              <Switch
                checked={grantKulanzVoucher}
                onChange={e => setGrantKulanzVoucher(e.target.checked)}
              />
            }
            label='Kulanz-Gutschein gewähren'
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseNoShowPaymentDialog}
            disabled={isConfirmingNoShowPayment || isLoading}
          >
            Abbrechen
          </Button>
          <Button
            variant='contained'
            color='success'
            onClick={handleConfirmNoShowPayment}
            disabled={isConfirmingNoShowPayment || isLoading}
          >
            Zahlungseingang bestätigen
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={noShowCancelDialog.open}
        onClose={handleCloseNoShowCancelDialog}
        maxWidth='xs'
        fullWidth
      >
        <DialogTitle>
          {noShowCancelDialog.invoice?.origin === 'noShow'
            ? 'No-Show-Vorgang abbrechen?'
            : 'Forderung erlassen & stornieren?'}
        </DialogTitle>
        <DialogContent>
          <Typography variant='body2' color='text.secondary' sx={{ mb: 1.5 }}>
            Die Forderung wird erlassen: Die Erinnerungs-E-Mails werden gestoppt und für die noch
            offenen Positionen wird eine Stornorechnung erstellt.
          </Typography>
          {noShowCancelDialog.invoice && (
            <Box sx={{ display: 'grid', gap: 0.5, mb: 1.5 }}>
              <Typography variant='body2'>
                <strong>Rechnung:</strong> {noShowCancelDialog.invoice.invoiceNumber}
              </Typography>
              <Typography variant='body2'>
                <strong>Betrag:</strong>{' '}
                {typeof noShowCancelDialog.invoice?.totals?.totalGross === 'number'
                  ? noShowCancelDialog.invoice.totals.totalGross.toFixed(2) + ' €'
                  : '-'}
              </Typography>
            </Box>
          )}
          <TextField
            fullWidth
            multiline
            minRows={2}
            label='Grund (optional)'
            defaultValue={noShowCancelReason}
            onBlur={e => setNoShowCancelReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseNoShowCancelDialog}
            disabled={isCancellingNoShow || isLoading}
          >
            Zurück
          </Button>
          <Button
            variant='contained'
            color='error'
            onClick={handleConfirmNoShowCancel}
            disabled={isCancellingNoShow || isLoading}
          >
            Vorgang abbrechen
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={pdfViewerOpen}
        onClose={handleClosePdfViewer}
        maxWidth='lg'
        fullWidth
        PaperProps={{ sx: { height: '85vh' } }}
      >
        <AppBar position='static' color='default' elevation={1}>
          <Toolbar>
            <Typography variant='h6' component='div' sx={{ flexGrow: 1 }}>
              {currentInvoice && `Rechnung ${currentInvoice.invoiceNumber}`}
            </Typography>
            <Tooltip title='Rechnung herunterladen'>
              <IconButton
                color='primary'
                onClick={() => currentInvoice && handleDownload(currentInvoice)}
                sx={{ mr: 2 }}
              >
                <DownloadIcon />
              </IconButton>
            </Tooltip>
            <IconButton edge='end' color='inherit' onClick={handleClosePdfViewer}>
              <CloseIcon />
            </IconButton>
          </Toolbar>
        </AppBar>
        <Box sx={{ p: 0, height: 'calc(85vh - 64px)' }}>
          {pdfUrl && (
            <iframe
              src={`${pdfUrl}#toolbar=0&navpanes=0`}
              style={{ width: '100%', height: '100%', border: 'none' }}
              title='PDF Viewer'
            />
          )}
        </Box>
      </Dialog>

      <Dialog
        open={collectionStatusDialog.open}
        onClose={handleCloseCollectionStatusDialog}
        maxWidth='xs'
        fullWidth
      >
        <DialogTitle>Mahnstufe / Crefo-Status bearbeiten</DialogTitle>
        <DialogContent>
          <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
            Passe die Mahnstufe und den Creditreform-Status dieser offenen Rechnung manuell an.
          </Typography>
          {collectionStatusDialog.invoice && (
            <Typography variant='body2' sx={{ mb: 2 }}>
              <strong>Rechnung:</strong> {collectionStatusDialog.invoice.invoiceNumber}
            </Typography>
          )}
          <TextField
            select
            fullWidth
            label='Mahnstufe'
            value={dunningStatusValue}
            onChange={e => setDunningStatusValue(e.target.value)}
            sx={{ mb: 2 }}
          >
            {DUNNING_STATUS_OPTIONS.map(opt => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            fullWidth
            label='Crefo-Status'
            value={krefoStatusValue}
            onChange={e => setKrefoStatusValue(e.target.value)}
          >
            {KREFO_STATUS_OPTIONS.map(opt => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseCollectionStatusDialog}
            disabled={isSavingCollectionStatus || isLoading}
          >
            Abbrechen
          </Button>
          <Button
            variant='contained'
            onClick={handleConfirmEditCollectionStatus}
            disabled={isSavingCollectionStatus || isLoading}
          >
            Speichern
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={krefoExportDialogOpen}
        onClose={() => {
          if (!isExportingKrefo) setKrefoExportDialogOpen(false);
        }}
        maxWidth='sm'
        fullWidth
      >
        <DialogTitle>Crefo-Export erstellen?</DialogTitle>
        <DialogContent>
          <Typography variant='body2' color='text.secondary' sx={{ mb: 1.5 }}>
            Es werden alle überfälligen, noch nicht an Creditreform übergebenen offenen Rechnungen
            als ZIP heruntergeladen – nach Standort in Unterordnern, mit den einzelnen Belegen.
            No-Show- und Unbezahlte-Buchung-Rechnungen werden automatisch überfällig; jede andere
            offene Rechnung ist enthalten, sobald ihre Mahnstufe manuell auf „Frist überschritten"
            gesetzt wurde.
          </Typography>
          <Typography variant='body2' sx={{ fontWeight: 600, color: 'warning.main' }}>
            Hinweis: Die enthaltenen Rechnungen werden dabei als „an Crefo übergeben“ markiert. Dieser
            Status kann später pro Rechnung wieder geändert werden.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setKrefoExportDialogOpen(false)} disabled={isExportingKrefo}>
            Abbrechen
          </Button>
          <Button
            variant='contained'
            startIcon={<FolderZipIcon />}
            onClick={handleConfirmKrefoExport}
            disabled={isExportingKrefo}
          >
            Export herunterladen
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default InvoiceManagement;
